import type { ManualDoc } from "../types.ts"
import { t } from "./t.ts"

export const FINDING_DOCS: ManualDoc[] = [
  {
    slug: "cases-list",
    category: "finding",
    order: 1,
    title: t("반품 목록 보기", "返品一覧の見かた"),
    summary: t(
      "표를 정렬하고, 보여 줄 칸을 고르고, 한 건을 옆판으로 열어 봅니다.",
      "表を並べ替え、表示する列を選び、1件を横のパネルで開きます。",
    ),
    keywords: ["목록", "정렬", "컬럼", "페이지", "옆판", "一覧", "並べ替え", "列"],
    body: [
      {
        kind: "p",
        text: t(
          "왼쪽 메뉴의 반품 목록은 모든 반품 건이 모이는 곳입니다. 제목 아래 요약 칩은 지금 몇 건인지와 손이 필요한 건수를 보여 주고, 누르면 그 조건으로 걸러집니다.",
          "左メニューの返品一覧はすべての返品が集まる場所です。タイトル下の要約チップは現在の件数と対応が必要な件数を示し、押すとその条件で絞り込まれます。",
        ),
      },      {
        kind: "image",
        src: t("/help/cases-list-ko.png", "/help/cases-list-ja.png"),
        alt: t("반품 목록 화면", "返品一覧の画面"),
        caption: t(
          "머리글을 누르면 정렬되고, 오른쪽 위 컬럼 설정으로 보여 줄 칸을 고릅니다.",
          "見出しを押すと並べ替えができ、右上の列の設定で表示する列を選べます。",
        ),
      },

      {
        kind: "list",
        items: [
          t(
            "줄을 누르면 오른쪽에 옆판이 열려 그 건의 요약·인보이스 품목·이력을 볼 수 있습니다. 목록으로 돌아가지 않고 다음 줄을 눌러도 됩니다.",
            "行を押すと右側にパネルが開き、その件の概要・インボイス品目・履歴を確認できます。一覧に戻らず次の行を押すこともできます。",
          ),
          t(
            "머리글을 누르면 정렬됩니다. 한 번 누르면 오름차순, 다시 누르면 내림차순, 한 번 더 누르면 원래 순서(접수일 최신순)로 돌아갑니다.",
            "見出しを押すと並べ替えできます。1回で昇順、もう1回で降順、さらにもう1回で元の順序(受付日の新しい順)に戻ります。",
          ),
          t(
            "표 아래에서 쪽을 넘기고, 한 쪽에 20·50·100건 중에서 고릅니다.",
            "表の下でページを移動し、1ページあたり20・50・100件から選べます。",
          ),
          t(
            "컬럼 설정에서 보여 줄 칸을 고릅니다. 반품사유·연락처·반입일·검수·착/선불·착불비·품목·한국반송/폐기일처럼 평소에는 접혀 있는 칸을 켤 수 있습니다.",
            "「列の設定」で表示する列を選べます。返品理由・連絡先・入庫日・検品・着払い/元払い・着払い費用・品目・韓国返送/廃棄日など、通常は隠れている列を表示できます。",
          ),
        ],
      },
      {
        kind: "note",
        tone: "info",
        text: t(
          "컬럼 설정은 이 브라우저에 계정별로 남습니다. 같은 PC 를 여러 사람이 써도 서로의 설정이 섞이지 않습니다.",
          "列の設定はこのブラウザにアカウントごとに保存されます。同じPCを複数人で使っても設定は混ざりません。",
        ),
      },
      {
        kind: "p",
        text: t(
          "휴대폰에서는 표가 한 건씩 카드로 바뀝니다. 좁은 화면에서는 일부 칸이 접히지만, 카드에서는 접힌 칸도 함께 보입니다.",
          "スマートフォンでは表が1件ずつのカードに変わります。狭い画面では一部の列が隠れますが、カードでは隠れた項目も表示されます。",
        ),
      },
    ],
  },

  {
    slug: "search-filter",
    category: "finding",
    order: 2,
    title: t("검색·필터·저장된 뷰", "検索・絞り込み・保存したビュー"),
    summary: t(
      "찾는 건을 좁히는 방법과, 자주 보는 조건을 이름 붙여 두는 방법.",
      "目的の件を絞り込む方法と、よく使う条件に名前を付けて保存する方法。",
    ),
    keywords: ["검색", "필터", "기간", "상태", "저장된 뷰", "통합검색", "検索", "絞り込み", "ビュー"],
    body: [
      { kind: "heading", text: t("반품 목록에서 좁히기", "返品一覧で絞り込む") },
      {
        kind: "list",
        items: [
          t(
            "검색칸 — 주문번호·반송장번호·고객명으로 찾습니다. 타이핑을 멈추면 알아서 걸러집니다. 반송장번호는 하이픈을 넣든 빼든 찾힙니다.",
            "検索欄 — 注文番号・返送伝票番号・顧客名で検索します。入力を止めると自動で絞り込まれます。返送伝票番号はハイフンの有無を問いません。",
          ),
          t("상태 — 접수완료·입고완료·서류완료·출고·반송완료·폐기·접수취소 중 하나를 고릅니다.", "状態 — 受付完了・入庫完了・書類完了・出庫・返送完了・廃棄・受付取消から選びます。"),
          t("기간 — 오늘·7일·30일 또는 직접을 골라 접수일 범위를 정합니다.", "期間 — 本日・7日・30日、または直接指定で受付日の範囲を決めます。"),
          t(
            "지금 걸린 조건은 필터 아래 칩으로 보입니다. 칩의 × 로 하나씩 풀고, 조건 모두 지우기로 한 번에 풉니다.",
            "現在の条件はフィルターの下にチップで表示されます。チップの×で1つずつ解除し、「条件をすべて消す」で一度に解除できます。",
          ),
        ],
      },      {
        kind: "image",
        src: t("/help/filters-ko.png", "/help/filters-ja.png"),
        alt: t("검색과 필터 칸", "検索と絞り込みの欄"),
        caption: t(
          "지금 걸린 조건은 아래 칩으로 보입니다. 저장된 뷰도 같은 자리에 놓입니다.",
          "現在の条件は下のチップで表示されます。保存したビューも同じ場所に並びます。",
        ),
      },

      {
        kind: "note",
        tone: "info",
        text: t(
          "조건은 주소(URL)에 남습니다. 브라우저 뒤로가기가 그대로 동작하고, 주소를 복사해 동료에게 보내면 같은 화면을 봅니다.",
          "条件はURLに残ります。ブラウザの戻るがそのまま効き、URLをコピーして同僚に送れば同じ画面を開けます。",
        ),
      },
      { kind: "heading", text: t("저장된 뷰", "保存したビュー") },
      {
        kind: "steps",
        items: [
          t("보고 싶은 조건을 걸어 둡니다(상태·기간·검색어·정렬 무엇이든).", "見たい条件を設定します(状態・期間・検索語・並べ替え、いずれも可)。"),
          t("지금 조건 저장을 누르고 이름을 적습니다(예: 미입고만).", "「今の条件を保存」を押して名前を入力します(例: 未入庫のみ)。"),
          t("다음부터는 칩을 한 번 누르면 그 조건으로 돌아갑니다.", "次からはチップを1回押すだけでその条件に戻れます。"),
        ],
      },
      {
        kind: "list",
        items: [
          t("지금 보고 있는 조건과 같은 뷰는 칩이 켜져 보입니다.", "現在の条件と同じビューはチップが強調表示されます。"),
          t("같은 이름으로 다시 저장하면 조건을 덮어씁니다. 칩의 × 로 지웁니다.", "同じ名前で保存し直すと条件を上書きします。チップの×で削除します。"),
          t("뷰도 이 브라우저에 계정별로 남습니다.", "ビューもこのブラウザにアカウントごとに保存されます。"),
        ],
      },
      { kind: "heading", text: t("어느 화면에서든 찾기", "どの画面からでも探す") },
      {
        kind: "p",
        text: t(
          "위쪽 줄의 통합 검색(Ctrl+K, 맥은 ⌘K)은 화면을 옮기지 않고 반품 건을 찾습니다. 주문번호·반송장번호·고객명으로 찾아 위아래 화살표로 고르고 Enter 로 그 건을 엽니다. 전화로 주문번호를 불러 주는 동안 쓰기 좋습니다.",
          "上部の統合検索(Ctrl+K、Macは⌘K)は画面を移動せずに返品を探せます。注文番号・返送伝票番号・顧客名で検索し、上下の矢印で選んでEnterで開きます。電話で注文番号を聞きながら使うのに便利です。",
        ),
      },      {
        kind: "image",
        src: t("/help/search-ko.png", "/help/search-ja.png"),
        alt: t("통합 검색", "統合検索"),
        caption: t(
          "Ctrl+K 로 어디서든 열립니다. 위아래 화살표로 고르고 Enter 로 그 건을 엽니다.",
          "Ctrl+Kでどこからでも開きます。上下の矢印で選び、Enterでその件を開きます。",
        ),
      },

    ],
  },

  {
    slug: "export",
    category: "finding",
    order: 3,
    title: t("CSV 내려받기", "CSVのダウンロード"),
    summary: t(
      "지금 걸어 둔 조건 그대로 목록을 파일로 받습니다.",
      "現在の絞り込み条件のまま一覧をファイルで取得します。",
    ),
    keywords: ["csv", "엑셀", "내보내기", "다운로드", "시트", "エクセル", "書き出し"],
    body: [
      {
        kind: "steps",
        items: [
          t("반품 목록에서 보고 싶은 조건을 겁니다.", "返品一覧で見たい条件を設定します。"),
          t("오른쪽 위 CSV 내려받기를 누릅니다.", "右上の「CSVをダウンロード」を押します。"),
          t("받은 returns.csv 를 엑셀에서 엽니다.", "ダウンロードした returns.csv をExcelで開きます。"),
        ],
      },
      {
        kind: "list",
        items: [
          t(
            "칸 순서는 예전에 쓰던 구글시트와 같게 맞춰 두었습니다. 반품사유·반품수량·검수수량도 함께 나갑니다.",
            "列の並びは以前使っていたGoogleスプレッドシートに合わせています。返品理由・返品数量・検品数量も出力されます。",
          ),
          t("한 번에 200건까지 나갑니다. 더 많으면 기간을 나눠 두 번 받으세요.", "1回につき200件までです。それ以上は期間を分けて2回に分けて取得してください。"),
          t("화주 계정은 자기 화주 건만 받습니다.", "荷主アカウントは自分の荷主の件のみ取得します。"),
        ],
      },
      {
        kind: "note",
        tone: "info",
        text: t(
          "검수수량 칸은 모든 줄을 센 건에만 숫자가 들어갑니다. 아직 검수 전이면 빈 칸입니다 — 0 과 구별하기 위해서입니다.",
          "検品数量の列は、全行を数えた件にのみ数値が入ります。検品前は空欄です — 0と区別するためです。",
        ),
      },
    ],
  },

  {
    slug: "item-masters",
    category: "finding",
    order: 4,
    title: t("품목 마스터", "品目マスタ"),
    summary: t(
      "인보이스에 쓰는 품명·HS코드·원산지·기본 단가를 모아 둔 목록입니다.",
      "インボイスで使う品名・HSコード・原産地・標準単価をまとめた一覧です。",
    ),
    keywords: ["품목", "마스터", "HS코드", "원산지", "단가", "品目", "マスタ", "原産地"],
    body: [
      {
        kind: "p",
        text: t(
          "반품 접수의 반품 상품과 인보이스 품목에서 고르는 목록이 여기입니다. 자주 쓰는 품목을 미리 등록해 두면 매번 품명과 HS코드를 치지 않아도 됩니다.",
          "返品受付の返品商品や、インボイス品目で選ぶ一覧がここです。よく使う品目を登録しておけば、毎回品名とHSコードを入力せずに済みます。",
        ),
      },
      {
        kind: "table",
        head: [t("칸", "項目"), t("설명", "説明")],
        rows: [
          [t("코드", "コード"), t("품목을 가리키는 짧은 이름(예: TOP-KNIT).", "品目を示す短いコード(例: TOP-KNIT)。")],
          [t("품명(한국어)", "品名(韓国語)"), t("목록과 고르는 칸에 보이는 이름.", "一覧や選択欄に表示される名前。")],
          [t("품명(영문)", "品名(英語)"), t("인보이스에 실리는 이름. 일본어 화면에서는 고를 때도 이 이름이 보입니다.", "インボイスに記載される名前。日本語画面では選択時もこの名前が表示されます。")],
          [t("HS코드", "HSコード"), t("세관 품목 분류 번호.", "税関の品目分類番号。")],
          [t("원산지", "原産地"), t("인보이스에 그대로 실립니다(예: KOREA).", "インボイスにそのまま記載されます(例: KOREA)。")],
          [t("기본 단가", "標準単価"), t("인보이스 품목에 넣을 때 미리 채워지는 값. 건마다 고칠 수 있습니다.", "インボイス品目に入れる際に初期値として入ります。件ごとに変更できます。")],
        ],
      },      {
        kind: "image",
        src: t("/help/item-masters-ko.png", "/help/item-masters-ja.png"),
        alt: t("품목 마스터 화면", "品目マスタの画面"),
        caption: t(
          "품명·HS코드로 검색해 찾습니다. 추가와 수정은 관리자만 할 수 있습니다.",
          "品名・HSコードで検索できます。追加と編集は管理者のみ可能です。",
        ),
      },

      { kind: "heading", text: t("품목 고치기", "品目の編集") },
      {
        kind: "steps",
        items: [
          t("품목 마스터에서 고칠 품목의 수정을 누릅니다(줄을 눌러도 열립니다).", "品目マスタで編集したい品目の「編集」を押します(行を押しても開きます)。"),
          t("지금 저장된 값이 채워진 채로 열립니다. 고칠 칸만 고칩니다.", "現在保存されている値が入った状態で開きます。直したい欄だけ修正します。"),
          t("저장을 누르면 목록에 바로 반영됩니다.", "保存を押すと一覧にすぐ反映されます。"),
        ],
      },
      {
        kind: "note",
        tone: "info",
        text: t(
          "코드는 고칠 수 없습니다. 사람들이 그 값으로 품목을 부르기 때문입니다. 이미 기재한 인보이스 줄은 품명을 고쳐도 따라 바뀌지 않습니다(신고한 내용은 그대로 남습니다).",
          "コードは変更できません。その値で品目を呼ぶためです。すでに記入済みのインボイス行は、品名を変更しても追随しません(申告済みの内容はそのまま残ります)。",
        ),
      },
      {
        kind: "list",
        items: [
          t("품목을 더하거나 고치는 것은 관리자만 할 수 있습니다. 나머지 계정은 보기만 합니다.", "品目の追加・編集は管理者のみ可能です。他のアカウントは閲覧のみです。"),
          t(
            "인보이스에 넣을 때 그 시점의 품명·HS코드·원산지를 복사해 둡니다. 나중에 마스터를 고쳐도 이미 신고한 인보이스는 바뀌지 않습니다.",
            "インボイスに入れる時点の品名・HSコード・原産地をコピーして保存します。後でマスタを変更しても申告済みのインボイスは変わりません。",
          ),
        ],
      },
    ],
  },
]
